import {ApplicationRef, EventEmitter, Injectable} from '@angular/core';
import {DialogViewModelCreatorService} from './dialog-view-model-creator.service';
import {Observable, Subject} from 'rxjs';
import {ResponseDto} from '../dto/ResponseDto';
import {MessageAttachment} from '../dto/MessageAttachment';
import {CommunicationService} from './communication.service';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';
import {
    InputSourceType
} from 'botato-botengine-browser/botato/botato-botengine/source/InstantMessagingAdapter/WebSocket/dto/InputSourceType.js';

@Injectable()
export class BotatoChatManagerService {

    private focusInputSubject: Subject<void> = new Subject();
    public focusInput$: Observable<void> = this.focusInputSubject.asObservable();

    private authenticationFailedSubject: Subject<void> = new Subject();
    public authenticationFailed$: Observable<void> = this.authenticationFailedSubject.asObservable();

    private scrollToBottomSubject: Subject<boolean> = new Subject();
    public scrollToBottom$: Observable<boolean> = this.scrollToBottomSubject.asObservable();

    public alreadyConnectChanged$: Observable<boolean>;
    public messageReceived$: Observable<ResponseDto>;

    private conversationHasHistory: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public conversationHasHistory$: Observable<boolean> = this.conversationHasHistory.asObservable();

    private isConnected: Subject<boolean> = new BehaviorSubject(false);
    public isConnected$: Observable<boolean> = this.isConnected.asObservable();

    private scrolledMessages: number[] = [];
    private alreadyConnectedOnOtherClient: boolean = false;

    private waitingForBotResponseSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public waitingForBotResponse$: Observable<boolean> = this.waitingForBotResponseSubject.asObservable();

    public unreadStatus$: Observable<boolean>;
    public lastReadAt$: Observable<string | undefined>;

    constructor(private communicationService: CommunicationService,
                private dialogViewModelCreator: DialogViewModelCreatorService,
                private translationService: TranslateService,
                private applicationRef: ApplicationRef) {
        this.communicationService.alreadyConnectChanged$.subscribe((isAlreadyConnectedOnOtherClient) => this.alreadyConnectedOnOtherClient = isAlreadyConnectedOnOtherClient);
        this.alreadyConnectChanged$ = this.communicationService.alreadyConnectChanged$;
        this.communicationService.connectionStateChanged.subscribe((isConnected) => {
            this.isConnected.next(isConnected);
        });

        this.communicationService.authenticationFailed.subscribe(() => {
            this.authenticationFailedSubject.next();
        });

        this.messageReceived$ = this.communicationService.messageReceived;
        this.communicationService.messageReceived.subscribe((botResult: ResponseDto) => {
            this.communicationService.lastBotResult = botResult;
            this.dialogViewModelCreator.addBotMessage(botResult);
            this.applicationRef.tick();
        });

        this.communicationService.thinkingIndicator.subscribe(() => {
            this.dialogViewModelCreator.showThinkingIndicator();
            this.setWaitingForBotResponseState(true); // keep input locked: the agent is still sending (more messages are coming)
            this.applicationRef.tick();
        });

        this.communicationService.startTyping.subscribe(() => {
            this.dialogViewModelCreator.showTypingIndicator();
            this.setWaitingForBotResponseState(true); // keep input locked: the agent is mid-turn
            this.applicationRef.tick();
        });

        let pendingLastReadAt: string | undefined;
        this.communicationService.lastReadAtReceived.subscribe((lastReadAt: string | undefined) => {
            pendingLastReadAt = lastReadAt;
        });

        this.communicationService.historyReceived.subscribe((history: (ResponseDto | string)[]) => {
            if (history.length > 0) {
                this.dialogViewModelCreator.addHistory(history, pendingLastReadAt);
                pendingLastReadAt = undefined;
                this.conversationHasHistory.next(true);
                this.communicationService.lastHistoryBotResult = this.getLastBotResultFromHistory(history);
                this.applicationRef.tick();
            }
        });

        this.communicationService.messageSendingStarted.subscribe(() => {
            this.setWaitingForBotResponseState(true);
        });

        this.communicationService.messageSendingFinished.subscribe(() => {
            this.setWaitingForBotResponseState(false);
        });

        this.unreadStatus$ = this.communicationService.unreadStatusReceived;
        this.lastReadAt$ = this.communicationService.lastReadAtReceived;
    }

    private getLastBotResultFromHistory(history: (ResponseDto | string)[]): ResponseDto | undefined {
        const responseDtoElements = history.filter((item) => typeof item !== 'string') as ResponseDto[];
        return responseDtoElements[responseDtoElements.length - 1];
    }

    public isAlreadyConnectedOnOtherClient(): boolean {
        return this.alreadyConnectedOnOtherClient;
    }

    public async start(): Promise<void> {
        await this.communicationService.welcome();
    }

    public async connect(overtakeConnection: boolean = false): Promise<boolean> {
        return this.communicationService.connect(overtakeConnection);
    }

    public setBotId(botId: string): void {
        return this.communicationService.setBotId(botId);
    }

    public async disconnect(): Promise<void> {
        return this.communicationService.disconnect();
    }

    public focusInput(): void {
        this.focusInputSubject.next();
    }

    public async sendUserMessage(message: string, inputSource: InputSourceType, attachment?: MessageAttachment): Promise<void> {
        if (message.trim().length > 0) {
            this.dialogViewModelCreator.addUserTextMessage(message);
        }
        await this.communicationService.sendMessage(message, inputSource, attachment);
    }

    public scrollToBottomOfMessage(messageId: number, useSmoothScrolling: boolean = true): void {
        if (!this.scrolledMessages.includes(messageId)) {
            this.scrollToBottom(useSmoothScrolling);
            this.scrolledMessages.push(messageId);
        }
    }

    public scrollToBottom(useSmoothScrolling: boolean = true): void {
        this.scrollToBottomSubject.next(useSmoothScrolling);
    }

    public readMessages(): EventEmitter<ResponseDto> {
        return this.communicationService.messageReceived;
    }

    public setLanguage(language: string): void {
        if (!this.translationService.getLangs().some(availableLanguage => availableLanguage === language)) {
            throw new Error(`Tried to set a not available language ${language} - available languages: ${this.translationService.getLangs()}`);
        }

        this.translationService.use(language);
        this.applicationRef.tick();
    }

    public clearDialog(): void {
        this.dialogViewModelCreator.clearDialog();
    }

    public setAccessTokenProvider(provider: () => Promise<string>): void {
        this.communicationService.setAccessTokenProvider(provider);
    }

    public async sendMarkAsRead(): Promise<void> {
        await this.communicationService.sendMarkAsRead();
    }

    public showTypingIndicator(): void {
        this.dialogViewModelCreator.showTypingIndicator();
    }

    public markDividerBeforeNextBotMessage(): void {
        this.dialogViewModelCreator.markDividerBeforeNextBotMessage();
    }

    public cancelPendingDivider(): void {
        this.dialogViewModelCreator.cancelPendingDivider();
    }

    public removeNewMessagesDivider(): void {
        this.dialogViewModelCreator.removeNewMessagesDivider();
    }

    private setWaitingForBotResponseState(state: boolean): void {
        if (this.waitingForBotResponseSubject.getValue() !== state) {
            this.waitingForBotResponseSubject.next(state);
        }
    }
}
